import { useEffect, useState, useRef } from 'react';

// Handles dynamic two-way scroll revealing 
export const useReveal = () => {
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Element entered the viewport: Make it visible
          entry.target.classList.add('visible');
          entry.target.classList.remove('hidden-top', 'hidden-bottom');
        } else {
          // Element left the viewport: Hide it
          entry.target.classList.remove('visible');
          
          // Determine direction: Did it leave through the top or bottom of the screen?
          // entry.boundingClientRect.top < 0 means it scrolled up past the view
          if (entry.boundingClientRect.top < 0) {
            entry.target.classList.add('hidden-top');
            entry.target.classList.remove('hidden-bottom');
          } else {
            // Otherwise, it went back down below the screen
            entry.target.classList.add('hidden-bottom');
            entry.target.classList.remove('hidden-top');
          }
        }
      });
    }, { 
      threshold: 0.1, 
      // -50px ensures the animation triggers slightly after crossing the edge for better visibility
      rootMargin: '-50px 0px -50px 0px' 
    });

    const elements = document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    
    // Initialize elements as hidden at the bottom on initial page load
    elements.forEach(el => {
      el.classList.add('hidden-bottom');
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []); // Run once on mount
};

// Handles counting up numbers when scrolled into view, resetting when hidden
export const useCounter = (target, speed = 200) => {
  const ref = useRef(null);

  useEffect(() => {
    const currentRef = ref.current;
    if (!currentRef) return;
    
    let updateCountInterval;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        let start = 0;
        const increment = target / speed;
        
        // Clear any old intervals before starting to count
        clearInterval(updateCountInterval);
        
        updateCountInterval = setInterval(() => {
          start += increment;
          if (start >= target) {
            currentRef.innerText = target;
            clearInterval(updateCountInterval);
          } else {
            currentRef.innerText = Math.ceil(start);
          }
        }, 15);
      } else {
        // Reset to 0 when scrolled out of view
        currentRef.innerText = '0'; 
        clearInterval(updateCountInterval);
      }
    }, { 
        // 0.1 threshold ensures it triggers even if only a small part of the number is visible
        threshold: 0.1 
    });

    observer.observe(currentRef);
    
    return () => { 
      observer.unobserve(currentRef); 
      clearInterval(updateCountInterval);
    };
  }, [target, speed]);

  return ref;
};